<template>
  <button
    :type="nativeType"
    :class="[
      'app-btn', 
      `app-btn-${variant}`,
      { 
        'app-btn-block': block,
        'app-btn-icon-only': iconOnly,
        'app-btn-loading': loading,
        'app-btn-rounded': rounded,
        'app-btn-disabled': disabled,
        [`app-btn-${size}`]: size
      }
    ]"
    :disabled="disabled || loading"
    @click="handleClick"
    :aria-busy="loading ? 'true' : 'false'"
    :aria-disabled="disabled ? 'true' : 'false'"
    aria-label="button"
  >
    <span v-if="loading" class="app-btn-spinner" aria-hidden="true">
      <span class="spinner-dot"></span>
      <span class="spinner-dot"></span>
      <span class="spinner-dot"></span>
    </span>
    
    <i v-if="icon && !iconRight" :class="['app-btn-icon', icon]" aria-hidden="true"></i>
    
    <span class="app-btn-text" :class="{ 'sr-only': iconOnly }">
      <slot></slot>
    </span>
    
    <i v-if="icon && iconRight" :class="['app-btn-icon', 'app-btn-icon-right', icon]" aria-hidden="true"></i>
  </button>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'AppButton',
  props: {
    variant: {
      type: String,
      default: 'primary',
      validator: value => [
        'primary', 'secondary', 'success', 'danger', 
        'warning', 'info', 'light', 'dark', 'link',
        'outline-primary', 'outline-secondary', 'outline-success', 
        'outline-danger', 'outline-warning', 'outline-info'
      ].includes(value)
    },
    size: {
      type: String,
      default: 'md',
      validator: value => ['sm', 'md', 'lg'].includes(value)
    },
    icon: {
      type: String,
      default: ''
    },
    iconOnly: {
      type: Boolean,
      default: false
    },
    iconRight: {
      type: Boolean,
      default: false
    },
    block: {
      type: Boolean,
      default: false
    },
    rounded: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    nativeType: {
      type: String,
      default: 'button',
      validator: value => ['button', 'submit', 'reset'].includes(value)
    }
  },
  methods: {
    handleClick(event) {
      if (this.disabled || this.loading) {
        event.preventDefault()
        return
      }
      this.$emit('click', event)
    }
  }
});
</script>

<style scoped>
.app-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  font-family: var(--font-family-base);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  line-height: 1.5;
  text-align: center;
  text-decoration: none;
  vertical-align: middle;
  cursor: pointer;
  user-select: none;
  border: 1px solid transparent;
  border-radius: var(--border-radius-md);
  transition: color var(--transition-fast) var(--transition-timing),
              background-color var(--transition-fast) var(--transition-timing),
              border-color var(--transition-fast) var(--transition-timing),
              box-shadow var(--transition-fast) var(--transition-timing),
              transform var(--transition-fast) var(--transition-timing);
}

.app-btn:focus {
  outline: 0;
  box-shadow: 0 0 0 3px rgba(var(--primary-rgb, 79, 70, 229), 0.25);
}

.app-btn:active {
  transform: translateY(1px);
}

.app-btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: var(--font-size-sm);
  border-radius: var(--border-radius-sm);
}

.app-btn-lg {
  padding: 0.625rem 1.25rem;
  font-size: var(--font-size-lg);
  border-radius: var(--border-radius-lg);
}

.app-btn-block {
  display: flex;
  width: 100%;
}

.app-btn-rounded {
  border-radius: var(--border-radius-full);
}

.app-btn-primary {
  color: #fff;
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}

.app-btn-primary:hover {
  background-color: var(--primary-darker);
  border-color: var(--primary-darker);
}

.app-btn-secondary {
  color: #fff;
  background-color: var(--secondary-color);
  border-color: var(--secondary-color);
}

.app-btn-secondary:hover {
  background-color: var(--secondary-darker);
  border-color: var(--secondary-darker);
}

.app-btn-success {
  color: #fff;
  background-color: var(--success-color);
  border-color: var(--success-color);
}

.app-btn-success:hover {
  background-color: var(--success-darker);
  border-color: var(--success-darker);
}

.app-btn-danger {
  color: #fff;
  background-color: var(--danger-color);
  border-color: var(--danger-color);
}

.app-btn-danger:hover {
  background-color: var(--danger-darker);
  border-color: var(--danger-darker);
}

.app-btn-warning {
  color: #212529;
  background-color: var(--warning-color);
  border-color: var(--warning-color);
}

.app-btn-warning:hover {
  background-color: var(--warning-darker);
  border-color: var(--warning-darker);
}

.app-btn-info {
  color: #fff;
  background-color: var(--info-color);
  border-color: var(--info-color);
}

.app-btn-info:hover {
  background-color: var(--info-darker);
  border-color: var(--info-darker);
}

.app-btn-light {
  color: #212529;
  background-color: var(--surface-color);
  border-color: var(--surface-color);
}

.app-btn-light:hover {
  background-color: var(--divider-color);
  border-color: var(--divider-color);
}

.app-btn-dark {
  color: #fff;
  background-color: var(--text-color);
  border-color: var(--text-color);
}

.app-btn-dark:hover {
  background-color: var(--text-light);
  border-color: var(--text-light);
}

.app-btn-link {
  color: var(--primary-color);
  background-color: transparent;
  border-color: transparent;
  box-shadow: none;
  text-decoration: none;
}

.app-btn-link:hover {
  color: var(--primary-darker);
  text-decoration: underline;
}

/* Outline Buttons */
.app-btn-outline-primary {
  color: var(--primary-color);
  background-color: transparent;
  background-image: none;
  border-color: var(--primary-color);
}

.app-btn-outline-primary:hover {
  color: #fff;
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}

.app-btn-outline-secondary {
  color: var(--secondary-color);
  background-color: transparent;
  background-image: none;
  border-color: var(--secondary-color);
}

.app-btn-outline-secondary:hover {
  color: #fff;
  background-color: var(--secondary-color);
  border-color: var(--secondary-color);
}

.app-btn-outline-success {
  color: var(--success-color);
  background-color: transparent;
  background-image: none;
  border-color: var(--success-color);
}

.app-btn-outline-success:hover {
  color: #fff;
  background-color: var(--success-color);
  border-color: var(--success-color);
}

.app-btn-outline-danger {
  color: var(--danger-color);
  background-color: transparent;
  background-image: none;
  border-color: var(--danger-color);
}

.app-btn-outline-danger:hover {
  color: #fff;
  background-color: var(--danger-color);
  border-color: var(--danger-color);
}

.app-btn-outline-warning {
  color: var(--warning-color);
  background-color: transparent;
  background-image: none;
  border-color: var(--warning-color);
}

.app-btn-outline-warning:hover {
  color: #212529;
  background-color: var(--warning-color);
  border-color: var(--warning-color);
}

.app-btn-outline-info {
  color: var(--info-color);
  background-color: transparent;
  background-image: none;
  border-color: var(--info-color);
}

.app-btn-outline-info:hover {
  color: #fff;
  background-color: var(--info-color);
  border-color: var(--info-color);
}

/* Disabled state */
.app-btn-disabled,
.app-btn:disabled {
  opacity: 0.65;
  pointer-events: none;
}

/* Icon positioning */
.app-btn-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.app-btn-icon:not(.app-btn-icon-right) {
  margin-right: 0.5rem;
}

.app-btn-icon-right {
  margin-left: 0.5rem;
}

.app-btn-icon-only {
  padding: 0.5rem;
  width: 2.5rem;
  height: 2.5rem;
}

.app-btn-icon-only.app-btn-sm {
  padding: 0.25rem;
  width: 1.75rem;
  height: 1.75rem;
}

.app-btn-icon-only.app-btn-lg {
  padding: 0.625rem;
  width: 3.25rem;
  height: 3.25rem;
}

/* Loading state */
.app-btn-loading {
  position: relative;
  color: transparent !important;
}

.app-btn-loading .app-btn-text {
  visibility: hidden;
}

.app-btn-spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
}

.spinner-dot {
  width: 0.3rem;
  height: 0.3rem;
  border-radius: 50%;
  background-color: currentColor;
  margin: 0 0.15rem;
  animation: spinner-dot 1.4s infinite ease-in-out both;
}

.spinner-dot:nth-child(1) {
  animation-delay: -0.32s;
}

.spinner-dot:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes spinner-dot {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

/* Media query for smaller touch targets on mobile */
@media (max-width: 768px) {
  .app-btn {
    min-height: 44px;
  }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .app-btn,
  .spinner-dot {
    transition: none !important;
    animation: none !important;
  }
}
</style> 